import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { showToast } from '../components/Toast';
import { Link } from 'react-router-dom';
import './AdminPanel.css';

const AdminPanel = () => {
  // Estado general
  const [tabActiva, setTabActiva] = useState('usuarios');
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estado para usuarios
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [mostrarModalUsuario, setMostrarModalUsuario] = useState(false);
  const [modoEdicionUsuario, setModoEdicionUsuario] = useState(false);
  const [formDataUsuario, setFormDataUsuario] = useState({
    nombre: '',
    email: '',
    empresa: '',
    telefono: '',
    password: '',
    activo: true,
    es_admin: false
  });
  const [busquedaUsuario, setBusquedaUsuario] = useState('');

  // Estado para recorridos
  const [recorridos, setRecorridos] = useState([]);
  const [recorridoSeleccionado, setRecorridoSeleccionado] = useState(null);
  const [mostrarModalRecorrido, setMostrarModalRecorrido] = useState(false);
  const [modoEdicionRecorrido, setModoEdicionRecorrido] = useState(false);
  const [formDataRecorrido, setFormDataRecorrido] = useState({
    titulo: '',
    descripcion: '',
    publico: false
  });
  const [busquedaRecorrido, setBusquedaRecorrido] = useState('');
  const [filtroRecorrido, setFiltroRecorrido] = useState('todos');

  useEffect(() => {
    cargarEstadisticas();
    if (tabActiva === 'usuarios') {
      cargarUsuarios();
    } else if (tabActiva === 'recorridos') {
      cargarRecorridos();
    }
  }, [tabActiva]);

  const cargarEstadisticas = async () => {
    try {
      const statsRes = await api.get('/admin/estadisticas');
      if (statsRes.data.success) {
        setEstadisticas(statsRes.data.estadisticas);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const usuariosRes = await api.get('/admin/usuarios');
      if (usuariosRes.data.success) {
        setUsuarios(usuariosRes.data.usuarios);
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      if (error.response?.status === 403) {
        showToast('No tienes permisos de administrador', 'error');
      } else {
        showToast('Error al cargar usuarios', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const cargarRecorridos = async () => {
    try {
      setLoading(true);
      const recorridosRes = await api.get('/admin/recorridos');
      if (recorridosRes.data.success) {
        setRecorridos(recorridosRes.data.recorridos);
      }
    } catch (error) {
      console.error('Error al cargar recorridos:', error);
      showToast('Error al cargar recorridos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Funciones para usuarios
  const abrirModalCrearUsuario = () => {
    setModoEdicionUsuario(false);
    setUsuarioSeleccionado(null);
    setFormDataUsuario({
      nombre: '',
      email: '',
      empresa: '',
      telefono: '',
      password: '',
      activo: true,
      es_admin: false
    });
    setMostrarModalUsuario(true);
  };

  const abrirModalEditarUsuario = (usuario) => {
    setModoEdicionUsuario(true);
    setUsuarioSeleccionado(usuario);
    setFormDataUsuario({
      nombre: usuario.nombre || '',
      email: usuario.email || '',
      empresa: usuario.empresa || '',
      telefono: usuario.telefono || '',
      password: '',
      activo: usuario.activo === 1 || usuario.activo === true,
      es_admin: usuario.es_admin === 1 || usuario.es_admin === true
    });
    setMostrarModalUsuario(true);
  };

  const cerrarModalUsuario = () => {
    setMostrarModalUsuario(false);
    setUsuarioSeleccionado(null);
    setFormDataUsuario({
      nombre: '',
      email: '',
      empresa: '',
      telefono: '',
      password: '',
      activo: true,
      es_admin: false
    });
  };

  const handleSubmitUsuario = async (e) => {
    e.preventDefault();
    
    if (!modoEdicionUsuario && !formDataUsuario.password) {
      showToast('La contraseña es requerida para nuevos usuarios', 'error');
      return;
    }

    try {
      if (modoEdicionUsuario) {
        const datosActualizacion = { ...formDataUsuario };
        if (!datosActualizacion.password) {
          delete datosActualizacion.password;
        }
        await api.put(`/admin/usuarios/${usuarioSeleccionado.id}`, datosActualizacion);
        showToast('Usuario actualizado exitosamente', 'success');
      } else {
        await api.post('/admin/usuarios', formDataUsuario);
        showToast('Usuario creado exitosamente', 'success');
      }
      cerrarModalUsuario();
      cargarUsuarios();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      showToast(error.response?.data?.message || 'Error al guardar usuario', 'error');
    }
  };

  const toggleActivo = async (usuario) => {
    try {
      await api.patch(`/admin/usuarios/${usuario.id}/activo`);
      showToast(usuario.activo ? 'Usuario bloqueado' : 'Usuario activado', 'success');
      cargarUsuarios();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      showToast('Error al cambiar estado del usuario', 'error');
    }
  };

  const eliminarUsuario = async (usuario) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${usuario.nombre}"?`)) {
      return;
    }

    try {
      await api.delete(`/admin/usuarios/${usuario.id}`);
      showToast('Usuario eliminado exitosamente', 'success');
      cargarUsuarios();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      showToast('Error al eliminar usuario', 'error');
    }
  };

  // Funciones para recorridos
  const abrirModalEditarRecorrido = async (recorrido) => {
    try {
      const res = await api.get(`/admin/recorridos/${recorrido.id}`);
      if (res.data.success) {
        setModoEdicionRecorrido(true);
        setRecorridoSeleccionado(res.data.recorrido);
        setFormDataRecorrido({
          titulo: res.data.recorrido.titulo || '',
          descripcion: res.data.recorrido.descripcion || '',
          publico: res.data.recorrido.publico === 1 || res.data.recorrido.publico === true
        });
        setMostrarModalRecorrido(true);
      }
    } catch (error) {
      console.error('Error al cargar recorrido:', error);
      showToast('Error al cargar recorrido', 'error');
    }
  };

  const cerrarModalRecorrido = () => {
    setMostrarModalRecorrido(false);
    setRecorridoSeleccionado(null);
    setFormDataRecorrido({
      titulo: '',
      descripcion: '',
      publico: false
    });
  };

  const handleSubmitRecorrido = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/admin/recorridos/${recorridoSeleccionado.id}`, formDataRecorrido);
      showToast('Recorrido actualizado exitosamente', 'success');
      cerrarModalRecorrido();
      cargarRecorridos();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al guardar recorrido:', error);
      showToast(error.response?.data?.message || 'Error al guardar recorrido', 'error');
    }
  };

  const eliminarRecorrido = async (recorrido) => {
    if (!window.confirm(`¿Estás seguro de eliminar el recorrido "${recorrido.titulo}"?`)) {
      return;
    }

    try {
      await api.delete(`/admin/recorridos/${recorrido.id}`);
      showToast('Recorrido eliminado exitosamente', 'success');
      cargarRecorridos();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al eliminar recorrido:', error);
      showToast('Error al eliminar recorrido', 'error');
    }
  };

  // Filtros
  const usuariosFiltrados = usuarios.filter(usuario =>
    usuario.nombre?.toLowerCase().includes(busquedaUsuario.toLowerCase()) ||
    usuario.email?.toLowerCase().includes(busquedaUsuario.toLowerCase()) ||
    usuario.empresa?.toLowerCase().includes(busquedaUsuario.toLowerCase())
  );

  const recorridosFiltrados = recorridos.filter(recorrido => {
    const coincideBusqueda = 
      recorrido.titulo?.toLowerCase().includes(busquedaRecorrido.toLowerCase()) ||
      recorrido.descripcion?.toLowerCase().includes(busquedaRecorrido.toLowerCase()) ||
      recorrido.usuario_nombre?.toLowerCase().includes(busquedaRecorrido.toLowerCase());
    
    const coincideFiltro = 
      filtroRecorrido === 'todos' ||
      (filtroRecorrido === 'publicos' && recorrido.publico) ||
      (filtroRecorrido === 'privados' && !recorrido.publico);

    return coincideBusqueda && coincideFiltro;
  });

  if (loading && tabActiva === 'usuarios' && usuarios.length === 0) {
    return (
      <div className="main-content">
        <div className="loading">Cargando panel de administración...</div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Panel de Administración</h1>
        </div>

        {/* Estadísticas */}
        {estadisticas && (
          <div className="admin-stats">
            <div className="stat-card">
              <h3>{estadisticas.total_usuarios_activos || 0}</h3>
              <p>Usuarios Activos</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.total_usuarios_bloqueados || 0}</h3>
              <p>Usuarios Bloqueados</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.total_admins || 0}</h3>
              <p>Administradores</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.total_recorridos || 0}</h3>
              <p>Total Recorridos</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.total_imagenes || 0}</h3>
              <p>Total Imágenes</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.recorridos_publicos || 0}</h3>
              <p>Recorridos Públicos</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.recorridos_privados || 0}</h3>
              <p>Recorridos Privados</p>
            </div>
            <div className="stat-card">
              <h3>{estadisticas.usuarios_con_recorridos || 0}</h3>
              <p>Usuarios con Recorridos</p>
            </div>
          </div>
        )}

        {/* Pestañas */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${tabActiva === 'usuarios' ? 'active' : ''}`}
            onClick={() => setTabActiva('usuarios')}
          >
            👥 Usuarios
          </button>
          <button
            className={`admin-tab ${tabActiva === 'recorridos' ? 'active' : ''}`}
            onClick={() => setTabActiva('recorridos')}
          >
            🗺️ Recorridos
          </button>
        </div>

        {/* Contenido de Usuarios */}
        {tabActiva === 'usuarios' && (
          <div className="admin-tab-content">
            <div className="admin-section-header">
              <div className="admin-search">
                <input
                  type="text"
                  placeholder="Buscar usuarios por nombre, email o empresa..."
                  value={busquedaUsuario}
                  onChange={(e) => setBusquedaUsuario(e.target.value)}
                  className="search-input"
                />
              </div>
              <button onClick={abrirModalCrearUsuario} className="btn btn-primary">
                + Nuevo Usuario
              </button>
            </div>

            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Empresa</th>
                    <th>Teléfono</th>
                    <th>Recorridos</th>
                    <th>Imágenes</th>
                    <th>Estado</th>
                    <th>Rol</th>
                    <th>Fecha Registro</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">
                        {busquedaUsuario ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
                      </td>
                    </tr>
                  ) : (
                    usuariosFiltrados.map((usuario) => (
                      <tr key={usuario.id} className={usuario.activo === 0 ? 'inactive' : ''}>
                        <td>{usuario.id}</td>
                        <td>{usuario.nombre}</td>
                        <td>{usuario.email}</td>
                        <td>{usuario.empresa || '-'}</td>
                        <td>{usuario.telefono || '-'}</td>
                        <td>{usuario.total_recorridos || 0}</td>
                        <td>{usuario.total_imagenes || 0}</td>
                        <td>
                          <span className={`badge ${usuario.activo === 1 ? 'badge-success' : 'badge-danger'}`}>
                            {usuario.activo === 1 ? 'Activo' : 'Bloqueado'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${usuario.es_admin === 1 ? 'badge-warning' : 'badge-info'}`}>
                            {usuario.es_admin === 1 ? 'Admin' : 'Usuario'}
                          </span>
                        </td>
                        <td>{new Date(usuario.fecha_registro).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => abrirModalEditarUsuario(usuario)}
                              className="btn btn-sm btn-primary"
                              title="Editar"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => toggleActivo(usuario)}
                              className={`btn btn-sm ${usuario.activo === 1 ? 'btn-warning' : 'btn-success'}`}
                              title={usuario.activo === 1 ? 'Bloquear' : 'Activar'}
                            >
                              {usuario.activo === 1 ? '🔒' : '🔓'}
                            </button>
                            <button
                              onClick={() => eliminarUsuario(usuario)}
                              className="btn btn-sm btn-danger"
                              title="Eliminar"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contenido de Recorridos */}
        {tabActiva === 'recorridos' && (
          <div className="admin-tab-content">
            {loading && recorridos.length === 0 ? (
              <div className="loading">Cargando recorridos...</div>
            ) : (
              <>
                <div className="admin-section-header">
                  <div className="admin-filters">
                    <input
                      type="text"
                      placeholder="Buscar recorridos..."
                      value={busquedaRecorrido}
                      onChange={(e) => setBusquedaRecorrido(e.target.value)}
                      className="search-input"
                    />
                    <select
                      value={filtroRecorrido}
                      onChange={(e) => setFiltroRecorrido(e.target.value)}
                      className="filter-select"
                    >
                      <option value="todos">Todos</option>
                      <option value="publicos">Públicos</option>
                      <option value="privados">Privados</option>
                    </select>
                  </div>
                </div>

                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Título</th>
                        <th>Usuario</th>
                        <th>Empresa</th>
                        <th>Imágenes</th>
                        <th>Estado</th>
                        <th>Fecha Creación</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recorridosFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="no-data">
                            {busquedaRecorrido || filtroRecorrido !== 'todos' 
                              ? 'No se encontraron recorridos' 
                              : 'No hay recorridos registrados'}
                          </td>
                        </tr>
                      ) : (
                        recorridosFiltrados.map((recorrido) => (
                          <tr key={recorrido.id}>
                            <td>{recorrido.id}</td>
                            <td>
                              <strong>{recorrido.titulo}</strong>
                              {recorrido.descripcion && (
                                <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                  {recorrido.descripcion.substring(0, 50)}
                                  {recorrido.descripcion.length > 50 ? '...' : ''}
                                </div>
                              )}
                            </td>
                            <td>{recorrido.usuario_nombre || '-'}</td>
                            <td>{recorrido.usuario_empresa || '-'}</td>
                            <td>{recorrido.total_imagenes || 0}</td>
                            <td>
                              <span className={`badge ${recorrido.publico ? 'badge-success' : 'badge-info'}`}>
                                {recorrido.publico ? 'Público' : 'Privado'}
                              </span>
                            </td>
                            <td>{new Date(recorrido.fecha_creacion).toLocaleDateString()}</td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/visor/id/${recorrido.id}`}
                                  className="btn btn-sm btn-info"
                                  title="Ver"
                                  target="_blank"
                                >
                                  👁️
                                </Link>
                                <button
                                  onClick={() => abrirModalEditarRecorrido(recorrido)}
                                  className="btn btn-sm btn-primary"
                                  title="Editar"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => eliminarRecorrido(recorrido)}
                                  className="btn btn-sm btn-danger"
                                  title="Eliminar"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Modal para crear/editar usuario */}
        {mostrarModalUsuario && (
          <div className="modal-overlay" onClick={cerrarModalUsuario}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modoEdicionUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
                <button onClick={cerrarModalUsuario} className="modal-close">×</button>
              </div>
              <form onSubmit={handleSubmitUsuario} className="modal-form">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    value={formDataUsuario.nombre}
                    onChange={(e) => setFormDataUsuario({ ...formDataUsuario, nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formDataUsuario.email}
                    onChange={(e) => setFormDataUsuario({ ...formDataUsuario, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Empresa</label>
                  <input
                    type="text"
                    value={formDataUsuario.empresa}
                    onChange={(e) => setFormDataUsuario({ ...formDataUsuario, empresa: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={formDataUsuario.telefono}
                    onChange={(e) => setFormDataUsuario({ ...formDataUsuario, telefono: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Contraseña {modoEdicionUsuario ? '(dejar vacío para no cambiar)' : '*'}</label>
                  <input
                    type="password"
                    value={formDataUsuario.password}
                    onChange={(e) => setFormDataUsuario({ ...formDataUsuario, password: e.target.value })}
                    required={!modoEdicionUsuario}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formDataUsuario.activo}
                      onChange={(e) => setFormDataUsuario({ ...formDataUsuario, activo: e.target.checked })}
                    />
                    Usuario Activo
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formDataUsuario.es_admin}
                      onChange={(e) => setFormDataUsuario({ ...formDataUsuario, es_admin: e.target.checked })}
                    />
                    Administrador
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={cerrarModalUsuario} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modoEdicionUsuario ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal para editar recorrido */}
        {mostrarModalRecorrido && recorridoSeleccionado && (
          <div className="modal-overlay" onClick={cerrarModalRecorrido}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Editar Recorrido</h2>
                <button onClick={cerrarModalRecorrido} className="modal-close">×</button>
              </div>
              <form onSubmit={handleSubmitRecorrido} className="modal-form">
                <div className="form-group">
                  <label>Título *</label>
                  <input
                    type="text"
                    value={formDataRecorrido.titulo}
                    onChange={(e) => setFormDataRecorrido({ ...formDataRecorrido, titulo: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea
                    value={formDataRecorrido.descripcion}
                    onChange={(e) => setFormDataRecorrido({ ...formDataRecorrido, descripcion: e.target.value })}
                    rows="4"
                    style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '5px', fontSize: '1rem' }}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formDataRecorrido.publico}
                      onChange={(e) => setFormDataRecorrido({ ...formDataRecorrido, publico: e.target.checked })}
                    />
                    Recorrido Público
                  </label>
                </div>
                <div className="form-group" style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '5px' }}>
                  <strong>Información del Usuario:</strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    <strong>Nombre:</strong> {recorridoSeleccionado.usuario_nombre || '-'}<br />
                    <strong>Email:</strong> {recorridoSeleccionado.usuario_email || '-'}<br />
                    <strong>Empresa:</strong> {recorridoSeleccionado.usuario_empresa || '-'}
                  </p>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={cerrarModalRecorrido} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Actualizar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
